import { useEffect } from "react";
import { CandidateStatusGroupName } from "../../constants";
import {
  Candidate,
  PickedCandidate,
  PickedCandidateWithNewestStatus,
} from "../../../TalentsStatusAPI";
import { sortRejectedCandidates } from "./sortPickedCandidates";
import {
  sortRecommendedActiveCandidates,
  sortRecommendedNewCandidates,
} from "./sortNewRecommendedCandidates";
import { JobAdApplicant } from "../../../../jobAds/jodAdApplicants/jobAdApplicantsAPI";
import { SelectedTalent } from "../../../types/editOrder";
import { FormatFunction, FormattedCandidate, SetGroupData } from "./types";
import { isLongOrderNewTalentStatus } from "../../../talentsStatusSlice";

type Props = {
  pickedCandidates: PickedCandidate[];
  recommendedCandidates: PickedCandidate[];
  jobAdApplicants: JobAdApplicant[];
  setGroupData: SetGroupData;
  formatPickedCandidate: FormatFunction<PickedCandidate, FormattedCandidate>;
  formatCandidate: FormatFunction<Candidate, FormattedCandidate>;
  formatJobAdApplicant: FormatFunction<JobAdApplicant, FormattedCandidate>;
  updateSelectedItems: (selectedItems: SelectedTalent[]) => void;
  sortPickedCandidates: (
    candidates: PickedCandidate[],
  ) => PickedCandidateWithNewestStatus[];
};

const useUpdateLocalTalentStatusGroups = ({
  pickedCandidates,
  recommendedCandidates,
  jobAdApplicants,
  setGroupData,
  formatPickedCandidate,
  formatCandidate,
  formatJobAdApplicant,
  updateSelectedItems,
  sortPickedCandidates,
}: Props) => {
  // clear the selected items in the GroupedTable whenever the candidates change
  useEffect(() => {
    if (
      pickedCandidates.length ||
      recommendedCandidates.length ||
      jobAdApplicants.length
    ) {
      updateSelectedItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedCandidates, recommendedCandidates, jobAdApplicants]);

  useEffect(() => {
    const sortedPickedCandidates = sortPickedCandidates(pickedCandidates);
    const notRejectedCandidates = sortedPickedCandidates.filter(
      (candidate) => !candidate.eezyRejected && !candidate.withdrew,
    );
    const rejectedCandidates = sortRejectedCandidates(
      sortedPickedCandidates.filter(
        (candidate) => !!candidate.eezyRejected && !candidate.withdrew,
      ),
    );
    const withdrewCandidates = sortedPickedCandidates.filter(
      (candidate) => !!candidate.withdrew,
    );

    setGroupData(
      CandidateStatusGroupName.PICKED,
      notRejectedCandidates.map(formatPickedCandidate),
    );
    setGroupData(
      CandidateStatusGroupName.REJECTED,
      rejectedCandidates.map(formatPickedCandidate),
    );
    setGroupData(
      CandidateStatusGroupName.WITHDREW,
      withdrewCandidates.map(formatPickedCandidate),
    );
  }, [
    pickedCandidates,
    formatPickedCandidate,
    setGroupData,
    sortPickedCandidates,
  ]);

  useEffect(() => {
    const newRecommendedCandidates = recommendedCandidates.filter((candidate) =>
      isLongOrderNewTalentStatus(candidate.status),
    );
    const recommendedCandidatesExcludingNew = recommendedCandidates.filter(
      (candidate) => !isLongOrderNewTalentStatus(candidate.status),
    );
    setGroupData(
      CandidateStatusGroupName.RECOMMENDED_NEW,
      sortRecommendedNewCandidates(newRecommendedCandidates).map(
        formatCandidate,
      ),
    );
    setGroupData(
      CandidateStatusGroupName.RECOMMENDED,
      sortRecommendedActiveCandidates(recommendedCandidatesExcludingNew).map(
        formatCandidate,
      ),
    );
  }, [recommendedCandidates, formatCandidate, setGroupData]);

  useEffect(() => {
    setGroupData(
      CandidateStatusGroupName.JOB_AD,
      jobAdApplicants
        .sort((a, b) => a.appliedAt - b.appliedAt)
        .map(formatJobAdApplicant),
    );
  }, [jobAdApplicants, formatJobAdApplicant, setGroupData]);
};

export default useUpdateLocalTalentStatusGroups;
